/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Pure carousel focus arithmetic — shared by every screen that renders
 * one or more carousel rails in a vertical stack.
 *
 * Screens own D-pad input (ScreenController) and focus state (Redux); this
 * module owns only the index math.
 *
 * Framework-free by design: no React, no Redux, no DOM.
 */

/**
 * Moves focus left within a carousel row.
 * Returns the new tile index, or null when already at the first tile.
 */
export const moveCarouselTileLeft = (currentIndex: number): number | null =>
  currentIndex > 0 ? currentIndex - 1 : null;

/**
 * Moves focus right within a carousel row.
 * Returns the new tile index, or null when already at the last tile.
 */
export const moveCarouselTileRight = (
  currentIndex: number,
  itemCount: number
): number | null =>
  itemCount > 0 && currentIndex < itemCount - 1 ? currentIndex + 1 : null;

export type TCarouselRowMove = {
  carouselIndex: number;
  itemIndex: number;
  /** Scroll positions with the departing row's position saved. */
  savedScrollPositions: number[];
};

/** Clamps index into [0, length-1]; returns 0 for empty or negative-length sets. */
const clampCarouselIndex = (index: number, length: number): number => {
  if (length <= 0) {
    return 0;
  }
  if (index < 0) {
    return 0;
  }
  if (index >= length) {
    return length - 1;
  }
  return index;
};

/**
 * Moves focus to the previous carousel row.
 * Saves the departing row's scroll position and restores the target row's.
 * Returns null when already at the first row.
 */
export const moveCarouselRowUp = (
  focusedCarouselIndex: number,
  focusedItemIndex: number,
  scrollPositions: number[],
  itemCounts: number[]
): TCarouselRowMove | null => {
  const prevIndex: number = focusedCarouselIndex - 1;
  if (prevIndex < 0) {
    return null;
  }
  const updated: number[] = [...scrollPositions];
  updated[focusedCarouselIndex] = focusedItemIndex;
  const rawPrevCount: number | undefined = itemCounts[prevIndex];
  const prevCount: number = rawPrevCount !== undefined ? rawPrevCount : 0;
  const prevSaved: number | undefined = updated[prevIndex];
  return {
    carouselIndex: prevIndex,
    itemIndex: clampCarouselIndex(
      prevSaved !== undefined ? prevSaved : focusedItemIndex,
      prevCount
    ),
    savedScrollPositions: updated
  };
};

/**
 * Moves focus to the next carousel row.
 * Saves the departing row's scroll position and restores the target row's.
 * Returns null when already at the last row.
 */
export const moveCarouselRowDown = (
  focusedCarouselIndex: number,
  focusedItemIndex: number,
  scrollPositions: number[],
  itemCounts: number[]
): TCarouselRowMove | null => {
  const nextIndex: number = focusedCarouselIndex + 1;
  if (nextIndex >= itemCounts.length) {
    return null;
  }
  const updated: number[] = [...scrollPositions];
  updated[focusedCarouselIndex] = focusedItemIndex;
  const rawNextCount: number | undefined = itemCounts[nextIndex];
  const nextCount: number = rawNextCount !== undefined ? rawNextCount : 0;
  const nextSaved: number | undefined = updated[nextIndex];
  return {
    carouselIndex: nextIndex,
    itemIndex: clampCarouselIndex(
      nextSaved !== undefined ? nextSaved : focusedItemIndex,
      nextCount
    ),
    savedScrollPositions: updated
  };
};
